/**
 * Partner Locator v2 — Interactive D3 map + filterable dealer list
 * ────────────────────────────────────────────────────────────────
 * Standalone IIFE — no framework dependencies.
 * Requires: D3 v7, topojson-client v3 (loaded via CDN in the section).
 * Data source: Shopify Storefront API (GraphQL metaobjects).
 *
 * Geo data loaded at runtime:
 *  - World countries:  world-atlas@2/countries-110m.json (jsDelivr)
 *  - US states:        us-atlas@3/states-10m.json (jsDelivr)
 *  - CA provinces:     Natural Earth GeoJSON (GitHub Gist)
 *
 * To host the CA provinces file on Shopify CDN instead of the Gist,
 * upload the GeoJSON as a Shopify asset and change GEO_URLS.ca below.
 */
(function () {
  'use strict';

  // ════════════════════════════════════════════════════════════════
  // CONFIGURATION
  // ════════════════════════════════════════════════════════════════

  const STOREFRONT_TOKEN = '29fa1a2df5cb7863e154efb440bb0898';
  const API_VERSION = '2025-01';

  // Fallback CDN URLs if local geo assets are missing
  const GEO_URLS_FALLBACK = {
    world: 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json',
    us:    'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json',
    ca:    'https://gist.githubusercontent.com/Brideau/2391df60938462571ca9/raw/canadaprov.json',
  };

  const COLORS = {
    noDealer:      '#e8ecef',
    hasDealer:     '#4da8da',
    selected:      '#0077b6',
    selectedEmpty: '#d0d4d8',
    dimmed:        '#e8ecef',
    water:         '#f0f4f8',
  };

  // ════════════════════════════════════════════════════════════════
  // ID MAPPINGS — connect geo feature IDs to business codes
  // ════════════════════════════════════════════════════════════════

  /** ISO 3166-1 numeric → alpha-2 (covers world-atlas 110m features) */
  const ISO_NUM = {
    '4':'AF','8':'AL','12':'DZ','16':'AS','20':'AD','24':'AO','28':'AG','31':'AZ',
    '32':'AR','36':'AU','40':'AT','44':'BS','48':'BH','50':'BD','51':'AM','52':'BB',
    '56':'BE','64':'BT','68':'BO','70':'BA','72':'BW','76':'BR','84':'BZ','90':'SB',
    '96':'BN','100':'BG','104':'MM','108':'BI','112':'BY','116':'KH','120':'CM',
    '124':'CA','132':'CV','140':'CF','144':'LK','148':'TD','152':'CL','156':'CN',
    '158':'TW','170':'CO','174':'KM','178':'CG','180':'CD','188':'CR','191':'HR',
    '192':'CU','196':'CY','203':'CZ','204':'BJ','208':'DK','212':'DM','214':'DO',
    '218':'EC','222':'SV','226':'GQ','231':'ET','232':'ER','233':'EE','242':'FJ',
    '246':'FI','250':'FR','258':'PF','260':'TF','262':'DJ','266':'GA','268':'GE',
    '270':'GM','275':'PS','276':'DE','288':'GH','300':'GR','304':'GL','308':'GD',
    '320':'GT','324':'GN','328':'GY','332':'HT','340':'HN','348':'HU','352':'IS',
    '356':'IN','360':'ID','364':'IR','368':'IQ','372':'IE','376':'IL','380':'IT',
    '384':'CI','388':'JM','392':'JP','398':'KZ','400':'JO','404':'KE','408':'KP',
    '410':'KR','414':'KW','417':'KG','418':'LA','422':'LB','426':'LS','428':'LV',
    '430':'LR','434':'LY','438':'LI','440':'LT','442':'LU','450':'MG','454':'MW',
    '458':'MY','462':'MV','466':'ML','470':'MT','478':'MR','480':'MU','484':'MX',
    '492':'MC','496':'MN','498':'MD','499':'ME','504':'MA','508':'MZ','512':'OM',
    '516':'NA','520':'NR','524':'NP','528':'NL','540':'NC','548':'VU','554':'NZ',
    '558':'NI','562':'NE','566':'NG','578':'NO','586':'PK','591':'PA','598':'PG',
    '600':'PY','604':'PE','608':'PH','616':'PL','620':'PT','624':'GW','626':'TL',
    '630':'PR','634':'QA','642':'RO','643':'RU','646':'RW','659':'KN','662':'LC',
    '670':'VC','674':'SM','678':'ST','682':'SA','686':'SN','688':'RS','690':'SC',
    '694':'SL','702':'SG','703':'SK','704':'VN','705':'SI','706':'SO','710':'ZA',
    '716':'ZW','724':'ES','728':'SS','729':'SD','732':'EH','740':'SR','748':'SZ',
    '752':'SE','756':'CH','760':'SY','762':'TJ','764':'TH','768':'TG','776':'TO',
    '780':'TT','784':'AE','788':'TN','792':'TR','795':'TM','800':'UG','804':'UA',
    '807':'MK','818':'EG','826':'GB','834':'TZ','840':'US','854':'BF','858':'UY',
    '860':'UZ','862':'VE','882':'WS','887':'YE','894':'ZM',
  };

  /** ISO 3166-1 alpha-2 → display name (fallback when country not in Shopify data) */
  const ISO_CODE_TO_NAME = {
    AF:'Afghanistan',AL:'Albania',DZ:'Algeria',AS:'American Samoa',AD:'Andorra',AO:'Angola',AG:'Antigua and Barbuda',AZ:'Azerbaijan',
    AR:'Argentina',AU:'Australia',AT:'Austria',BS:'Bahamas',BH:'Bahrain',BD:'Bangladesh',AM:'Armenia',BB:'Barbados',
    BE:'Belgium',BT:'Bhutan',BO:'Bolivia',BA:'Bosnia and Herzegovina',BW:'Botswana',BR:'Brazil',BZ:'Belize',SB:'Solomon Islands',
    BN:'Brunei',BG:'Bulgaria',MM:'Myanmar',BI:'Burundi',BY:'Belarus',KH:'Cambodia',CM:'Cameroon',CA:'Canada',CV:'Cape Verde',
    CF:'Central African Republic',LK:'Sri Lanka',TD:'Chad',CL:'Chile',CN:'China',TW:'Taiwan',CO:'Colombia',KM:'Comoros',
    CG:'Congo',CD:'DR Congo',CR:'Costa Rica',HR:'Croatia',CU:'Cuba',CY:'Cyprus',CZ:'Czech Republic',BJ:'Benin',
    DK:'Denmark',DM:'Dominica',DO:'Dominican Republic',EC:'Ecuador',SV:'El Salvador',GQ:'Equatorial Guinea',ET:'Ethiopia',ER:'Eritrea',
    EE:'Estonia',FJ:'Fiji',FI:'Finland',FR:'France',PF:'French Polynesia',TF:'French Southern Territories',DJ:'Djibouti',GA:'Gabon',
    GE:'Georgia',GM:'Gambia',PS:'Palestine',DE:'Germany',GH:'Ghana',GR:'Greece',GL:'Greenland',GD:'Grenada',
    GT:'Guatemala',GN:'Guinea',GY:'Guyana',HT:'Haiti',HN:'Honduras',HU:'Hungary',IS:'Iceland',IN:'India',
    ID:'Indonesia',IR:'Iran',IQ:'Iraq',IE:'Ireland',IL:'Israel',IT:'Italy',CI:'Ivory Coast',JM:'Jamaica',
    JP:'Japan',KZ:'Kazakhstan',JO:'Jordan',KE:'Kenya',KP:'North Korea',KR:'South Korea',KW:'Kuwait',KG:'Kyrgyzstan',
    LA:'Laos',LB:'Lebanon',LS:'Lesotho',LV:'Latvia',LR:'Liberia',LY:'Libya',LI:'Liechtenstein',LT:'Lithuania',
    LU:'Luxembourg',MG:'Madagascar',MW:'Malawi',MY:'Malaysia',MV:'Maldives',ML:'Mali',MT:'Malta',MR:'Mauritania',
    MU:'Mauritius',MX:'Mexico',MC:'Monaco',MN:'Mongolia',MD:'Moldova',ME:'Montenegro',MA:'Morocco',MZ:'Mozambique',
    OM:'Oman',NA:'Namibia',NR:'Nauru',NP:'Nepal',NL:'Netherlands',NC:'New Caledonia',VU:'Vanuatu',NZ:'New Zealand',
    NI:'Nicaragua',NE:'Niger',NG:'Nigeria',NO:'Norway',PK:'Pakistan',PA:'Panama',PG:'Papua New Guinea',PY:'Paraguay',
    PE:'Peru',PH:'Philippines',PL:'Poland',PT:'Portugal',GW:'Guinea-Bissau',TL:'Timor-Leste',PR:'Puerto Rico',QA:'Qatar',
    RO:'Romania',RU:'Russia',RW:'Rwanda',KN:'Saint Kitts and Nevis',LC:'Saint Lucia',VC:'Saint Vincent and the Grenadines',
    SM:'San Marino',ST:'São Tomé and Príncipe',SA:'Saudi Arabia',SN:'Senegal',RS:'Serbia',SC:'Seychelles',SL:'Sierra Leone',
    SG:'Singapore',SK:'Slovakia',VN:'Vietnam',SI:'Slovenia',SO:'Somalia',ZA:'South Africa',ZW:'Zimbabwe',ES:'Spain',
    SS:'South Sudan',SD:'Sudan',EH:'Western Sahara',SR:'Suriname',SZ:'Eswatini',SE:'Sweden',CH:'Switzerland',SY:'Syria',
    TJ:'Tajikistan',TH:'Thailand',TG:'Togo',TO:'Tonga',TT:'Trinidad and Tobago',AE:'United Arab Emirates',TN:'Tunisia',
    TR:'Turkey',TM:'Turkmenistan',UG:'Uganda',UA:'Ukraine',MK:'North Macedonia',EG:'Egypt',GB:'United Kingdom',TZ:'Tanzania',
    US:'United States',BF:'Burkina Faso',UY:'Uruguay',UZ:'Uzbekistan',VE:'Venezuela',WS:'Samoa',YE:'Yemen',ZM:'Zambia',
  };

  /** US FIPS code → ISO 3166-2 region code */
  const FIPS_TO_REGION = {
    '01':'US-AL','02':'US-AK','04':'US-AZ','05':'US-AR','06':'US-CA','08':'US-CO',
    '09':'US-CT','10':'US-DE','11':'US-DC','12':'US-FL','13':'US-GA','15':'US-HI',
    '16':'US-ID','17':'US-IL','18':'US-IN','19':'US-IA','20':'US-KS','21':'US-KY',
    '22':'US-LA','23':'US-ME','24':'US-MD','25':'US-MA','26':'US-MI','27':'US-MN',
    '28':'US-MS','29':'US-MO','30':'US-MT','31':'US-NE','32':'US-NV','33':'US-NH',
    '34':'US-NJ','35':'US-NM','36':'US-NY','37':'US-NC','38':'US-ND','39':'US-OH',
    '40':'US-OK','41':'US-OR','42':'US-PA','44':'US-RI','45':'US-SC','46':'US-SD',
    '47':'US-TN','48':'US-TX','49':'US-UT','50':'US-VT','51':'US-VA','53':'US-WA',
    '54':'US-WV','55':'US-WI','56':'US-WY','60':'US-AS','66':'US-GU','69':'US-MP',
    '72':'US-PR','78':'US-VI',
  };

  /** US state names for tooltip display */
  const US_STATE_NAMES = {
    'US-AL':'Alabama','US-AK':'Alaska','US-AZ':'Arizona','US-AR':'Arkansas',
    'US-CA':'California','US-CO':'Colorado','US-CT':'Connecticut','US-DE':'Delaware',
    'US-DC':'District of Columbia','US-FL':'Florida','US-GA':'Georgia','US-HI':'Hawaii',
    'US-ID':'Idaho','US-IL':'Illinois','US-IN':'Indiana','US-IA':'Iowa',
    'US-KS':'Kansas','US-KY':'Kentucky','US-LA':'Louisiana','US-ME':'Maine',
    'US-MD':'Maryland','US-MA':'Massachusetts','US-MI':'Michigan','US-MN':'Minnesota',
    'US-MS':'Mississippi','US-MO':'Missouri','US-MT':'Montana','US-NE':'Nebraska',
    'US-NV':'Nevada','US-NH':'New Hampshire','US-NJ':'New Jersey','US-NM':'New Mexico',
    'US-NY':'New York','US-NC':'North Carolina','US-ND':'North Dakota','US-OH':'Ohio',
    'US-OK':'Oklahoma','US-OR':'Oregon','US-PA':'Pennsylvania','US-RI':'Rhode Island',
    'US-SC':'South Carolina','US-SD':'South Dakota','US-TN':'Tennessee','US-TX':'Texas',
    'US-UT':'Utah','US-VT':'Vermont','US-VA':'Virginia','US-WA':'Washington',
    'US-WV':'West Virginia','US-WI':'Wisconsin','US-WY':'Wyoming',
  };

  // ════════════════════════════════════════════════════════════════
  // UTILITIES
  // ════════════════════════════════════════════════════════════════

  const esc = (s) =>
    String(s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

  const parseJsonList = (value) => {
    if (value == null) return [];
    const s = String(value).trim();
    if (!s) return [];
    if (s.charAt(0) === '[') {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.map((x) => String(x ?? '').trim()).filter(Boolean);
      } catch (_) { /* fall through */ }
    }
    return s.split(',').map((x) => x.trim()).filter(Boolean);
  };

  const uniqSorted = (arr) =>
    [...new Set(arr.filter((v) => v && String(v).trim()).map((v) => String(v).trim()))].sort();

  const setSelectOpts = (sel, values, placeholder = 'All') => {
    const cur = sel.value;
    sel.innerHTML =
      `<option value="">${esc(placeholder)}</option>` +
      values.map((v) => `<option value="${esc(v)}">${esc(v)}</option>`).join('');
    if (values.includes(cur)) sel.value = cur;
  };

  // ════════════════════════════════════════════════════════════════
  // DATA LAYER — Shopify Storefront GraphQL
  // ════════════════════════════════════════════════════════════════

  const gqlEndpoint = `${window.location.origin}/api/${API_VERSION}/graphql.json`;

  const gql = (query, variables = {}) =>
    fetch(gqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    }).then((r) => r.json());

  /**
   * Paginate through all metaobjects of a given type.
   * @param {string} type        – metaobject type handle (e.g. "dealer")
   * @param {boolean} withRefs   – include references sub-query for list fields
   */
  async function fetchAllMetaobjects(type, withRefs = false) {
    const refsFrag = withRefs
      ? `references(first: 50) { edges { node { ... on Metaobject { id type fields { key value } } } } }`
      : '';
    const query = `
      query Q($first: Int!, $after: String) {
        metaobjects(type: "${type}", first: $first, after: $after) {
          edges {
            cursor
            node {
              id
              fields { key value ${refsFrag} }
            }
          }
          pageInfo { hasNextPage }
        }
      }`;

    const all = [];
    let after = null;
    let safety = 0;

    while (safety++ < 30) {
      const json = await gql(query, { first: 100, after });
      const edges = json?.data?.metaobjects?.edges ?? [];
      const hasNext = json?.data?.metaobjects?.pageInfo?.hasNextPage;
      for (const e of edges) all.push(e.node);
      if (!hasNext || edges.length === 0) break;
      after = edges[edges.length - 1].cursor;
    }

    return all;
  }

  // ── Normalizers ──

  const normalizeDealer = (node) => {
    const fields = {};
    const refs = {};

    for (const f of node.fields ?? []) {
      fields[f.key] = f.value;
      if (f.references?.edges?.length) {
        refs[f.key] = f.references.edges.map((e) => {
          const r = {};
          for (const rf of e.node.fields ?? []) r[rf.key] = rf.value;
          return r;
        });
      }
    }

    const countries = (refs.countries || []).map((r) => (r.code || '').trim()).filter(Boolean);
    const regions   = (refs.regions || []).map((r) => (r.code || '').trim()).filter(Boolean);

    return {
      id: node.id,
      name:        (fields.name || '').trim(),
      phone:       (fields.phone || '').trim(),
      website:     (fields.website || '').trim(),
      address:     (fields.address || '').trim(),
      dealer_type: parseJsonList(fields.dealer_type),
      channel:     parseJsonList(fields.channel),
      segment:     parseJsonList(fields.segment),
      countries,
      regions,
    };
  };

  const normalizeCountry = (node) => {
    const f = {};
    for (const field of node.fields ?? []) f[field.key] = field.value;
    return { code: (f.code || '').trim(), label: (f.label || '').trim() };
  };

  const normalizeRegion = (node) => {
    const f = {};
    for (const field of node.fields ?? []) f[field.key] = field.value;
    const code = (f.code || '').trim();
    return { code, label: (f.label || '').trim(), country: code.split('-')[0] || '' };
  };

  /** Load all dealer + country + region data in parallel */
  async function loadAllData() {
    const [dealerNodes, countryNodes, regionNodes] = await Promise.all([
      fetchAllMetaobjects('dealer', true),
      fetchAllMetaobjects('country', false),
      fetchAllMetaobjects('region', false),
    ]);
    return {
      dealers:   dealerNodes.map(normalizeDealer),
      countries: countryNodes.map(normalizeCountry),
      regions:   regionNodes.map(normalizeRegion),
    };
  }

  // ════════════════════════════════════════════════════════════════
  // STATE MANAGEMENT
  // ════════════════════════════════════════════════════════════════

  const state = {
    dealers: [],
    countries: [],
    regions: [],
    filters: { search: '', channel: '', segment: '', country: '', region: '' },
    filtered: [],
    dealersByCountry: {},
    dealersByRegion: {},
    /** Country codes that have at least one dealer in current filtered list (for map coloring) */
    countriesWithFilteredResults: new Set(),
    /** Region codes (US-XX, CA-XX) that have at least one dealer in current filtered list */
    regionsWithFilteredResults: new Set(),
    /** Filtered dealer count per country/region (for tooltip when filters active) */
    dealersByCountryFiltered: {},
    dealersByRegionFiltered: {},
    /** Countries/regions matching non-geographic filters only (channel, search) — for map coloring */
    countriesWithNonGeoResults: new Set(),
    regionsWithNonGeoResults: new Set(),
    dealersByCountryNonGeo: {},
    dealersByRegionNonGeo: {},
    loading: true,
    mapReady: false,
    zoomToSelection: null, // set by map; used by reset to zoom out
  };

  const listeners = [];
  const subscribe = (fn) => listeners.push(fn);

  function buildDealerMaps() {
    const byCountry = {};
    const byRegion = {};
    for (const d of state.dealers) {
      for (const c of d.countries) byCountry[c] = (byCountry[c] || 0) + 1;
      for (const r of d.regions) byRegion[r] = (byRegion[r] || 0) + 1;
    }
    state.dealersByCountry = byCountry;
    state.dealersByRegion = byRegion;

    const missingCountries = state.dealers.filter((d) => d.countries.length === 0);
    if (missingCountries.length) {
      console.warn('Partner Locator: dealers with no country data:', missingCountries.map((d) => d.name));
    }
  }

  function getFiltered() {
    const { search, channel, segment, country, region } = state.filters;
    let out = state.dealers;

    if (channel) out = out.filter((d) => d.channel.includes(channel));
    if (segment) out = out.filter((d) => d.segment.includes(segment));
    if (country) out = out.filter((d) => d.countries.includes(country));
    if (region) out = out.filter((d) => d.regions.includes(region));
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((d) => {
        const hay = [d.name, d.address, d.phone, d.dealer_type.join(' '), d.channel.join(' ')].join(' ').toLowerCase();
        return hay.includes(q);
      });
    }

    return out.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  /** Internal: recalculate filtered list and notify all subscribers */
  function notify() {
    state.filtered = getFiltered();
    const countriesWithResults = new Set();
    const regionsWithResults = new Set();
    const byCountryFiltered = {};
    const byRegionFiltered = {};
    for (const d of state.filtered) {
      for (const c of d.countries) {
        countriesWithResults.add(c);
        byCountryFiltered[c] = (byCountryFiltered[c] || 0) + 1;
      }
      for (const r of d.regions) {
        regionsWithResults.add(r);
        byRegionFiltered[r] = (byRegionFiltered[r] || 0) + 1;
      }
    }
    state.countriesWithFilteredResults = countriesWithResults;
    state.regionsWithFilteredResults = regionsWithResults;
    state.dealersByCountryFiltered = byCountryFiltered;
    state.dealersByRegionFiltered = byRegionFiltered;

    // Non-geographic pass: apply only channel + segment + search (ignore country/region)
    // so the map can highlight countries that *would* have results.
    const { search, channel, segment } = state.filters;
    let nonGeo = state.dealers;
    if (channel) nonGeo = nonGeo.filter((d) => d.channel.includes(channel));
    if (segment) nonGeo = nonGeo.filter((d) => d.segment.includes(segment));
    if (search) {
      const q = search.toLowerCase();
      nonGeo = nonGeo.filter((d) => {
        const hay = [d.name, d.address, d.phone, d.dealer_type.join(' '), d.channel.join(' ')].join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    const countriesNonGeo = new Set();
    const regionsNonGeo = new Set();
    const byCountryNonGeo = {};
    const byRegionNonGeo = {};
    for (const d of nonGeo) {
      for (const c of d.countries) {
        countriesNonGeo.add(c);
        byCountryNonGeo[c] = (byCountryNonGeo[c] || 0) + 1;
      }
      for (const r of d.regions) {
        regionsNonGeo.add(r);
        byRegionNonGeo[r] = (byRegionNonGeo[r] || 0) + 1;
      }
    }
    state.countriesWithNonGeoResults = countriesNonGeo;
    state.regionsWithNonGeoResults = regionsNonGeo;
    state.dealersByCountryNonGeo = byCountryNonGeo;
    state.dealersByRegionNonGeo = byRegionNonGeo;

    for (const fn of listeners) fn(state);
  }

  /** Update one or more filter values. Only triggers notify if something changed. */
  function setFilters(updates) {
    let changed = false;
    for (const key of Object.keys(updates)) {
      if (state.filters[key] !== updates[key]) {
        state.filters[key] = updates[key];
        changed = true;
      }
    }
    if (changed) notify();
  }

  // ════════════════════════════════════════════════════════════════
  // URL STATE
  // ════════════════════════════════════════════════════════════════

  function parseUrlState() {
    const p = new URLSearchParams(window.location.search);
    return {
      search: p.get('search') || '',
      channel: p.get('channel') || '',
      segment: p.get('segment') || '',
      country: p.get('country') || '',
      region: p.get('region') || '',
    };
  }

  function syncUrl() {
    const f = state.filters;
    const p = new URLSearchParams();
    if (f.search) p.set('search', f.search);
    if (f.channel) p.set('channel', f.channel);
    if (f.segment) p.set('segment', f.segment);
    if (f.country) p.set('country', f.country);
    if (f.region) p.set('region', f.region);
    const qs = p.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }

  // ════════════════════════════════════════════════════════════════
  // MAP — D3 + TopoJSON
  // ════════════════════════════════════════════════════════════════

  function initMap(container, onMapReady) {
    const d3 = window.d3;
    const topojson = window.topojson;
    if (!d3 || !topojson) {
      console.warn('Partner Locator: D3 or topojson not loaded');
      if (typeof onMapReady === 'function') onMapReady();
      return null;
    }

    const root = container.closest('.pl2');
    const geoUrls = {
      world: root?.getAttribute('data-geo-world') || GEO_URLS_FALLBACK.world,
      us:    root?.getAttribute('data-geo-us') || GEO_URLS_FALLBACK.us,
      ca:    root?.getAttribute('data-geo-ca') || GEO_URLS_FALLBACK.ca,
    };

    let width, height;
    let svg, g, projection, pathGen, zoomBehavior;
    let worldFeatures = [], usFeatures = [], caFeatures = [];

    // ── Tooltip ──
    const tooltip = document.createElement('div');
    tooltip.className = 'pl2__tooltip';
    document.body.appendChild(tooltip);

    const showTooltip = (text, count, event) => {
      const countLabel = count != null
        ? `<span class="pl2__tooltip-count"> · ${count} partner${count !== 1 ? 's' : ''}</span>`
        : '';
      tooltip.innerHTML = esc(text) + countLabel;
      tooltip.classList.add('pl2__tooltip--visible');
      tooltip.style.left = `${event.clientX + 14}px`;
      tooltip.style.top = `${event.clientY - 32}px`;
    };
    const hideTooltip = () => tooltip.classList.remove('pl2__tooltip--visible');

    // ── Setup SVG ──
    function setup() {
      const rect = container.getBoundingClientRect();
      width = rect.width;
      height = rect.height;

      svg = d3.select(container).append('svg')
        .attr('viewBox', `0 0 ${width} ${height}`);

      g = svg.append('g');

      projection = d3.geoNaturalEarth1()
        .scale(width / 5.5)
        .translate([width / 2, height / 2]);

      pathGen = d3.geoPath().projection(projection);

      const scaleMin = 1;
      const scaleMax = 12;

      function updateZoomBtnState(k) {
        const wrap = container.closest('.pl2__map-wrap');
        if (!wrap) return;
        const btnIn = wrap.querySelector('[data-zoom="in"]');
        const btnOut = wrap.querySelector('[data-zoom="out"]');
        if (btnIn) btnIn.disabled = k >= scaleMax;
        if (btnOut) btnOut.disabled = k <= scaleMin;
      }

      zoomBehavior = d3.zoom()
        .scaleExtent([scaleMin, scaleMax])
        .on('zoom', (event) => {
          g.attr('transform', event.transform);
          const k = event.transform.k;
          container.style.setProperty('--pl2-zoom-k', k);
          updateZoomBtnState(k);
        });

      svg.call(zoomBehavior);
      updateZoomBtnState(1);

      const zoomBtns = container.closest('.pl2__map-wrap')?.querySelectorAll('.pl2__map-zoom-btn');
      if (zoomBtns) {
        zoomBtns.forEach((btn) => {
          btn.addEventListener('click', () => {
            const dir = btn.getAttribute('data-zoom') === 'in' ? 2 : 0.5;
            svg.transition().duration(300).call(zoomBehavior.scaleBy, dir);
          });
        });
      }

      // Sphere (ocean background) — clicking resets geographic selection
      g.append('path')
        .datum({ type: 'Sphere' })
        .attr('class', 'sphere')
        .attr('d', pathGen)
        .on('click', () => {
          setFilters({ country: '', region: '' });
          zoomToSelection();
        });

      // Graticule
      g.append('path')
        .datum(d3.geoGraticule10())
        .attr('class', 'graticule')
        .attr('d', pathGen);
    }

    // ── Load geo data ──
    async function loadGeo() {
      const [worldData, usData, caData] = await Promise.all([
        d3.json(geoUrls.world),
        d3.json(geoUrls.us),
        d3.json(geoUrls.ca).catch(() => null),
      ]);

      // Full country features from world-atlas
      const allCountries = topojson.feature(worldData, worldData.objects.countries).features;

      // US states
      usFeatures = topojson.feature(usData, usData.objects.states).features;

      // Canadian provinces (GeoJSON or TopoJSON)
      if (caData) {
        if (caData.type === 'Topology') {
          const key = Object.keys(caData.objects)[0];
          caFeatures = topojson.feature(caData, caData.objects[key]).features;
        } else {
          caFeatures = caData.features || [];
        }
      }

      // World countries: exclude US (840) and CA (124) if sub-features loaded
      worldFeatures = allCountries.filter((f) => {
        if (String(f.id) === '840' && usFeatures.length > 0) return false;
        if (String(f.id) === '124' && caFeatures.length > 0) return false;
        return true;
      });
    }

    // ── Get identifiers ──
    const getCountryCode = (feature) => {
      const raw = String(feature.id);
      const fromId = ISO_NUM[raw] || ISO_NUM[raw.replace(/^0+/, '')];
      if (fromId) return fromId;
      const props = feature.properties || {};
      return props.iso_a2 || props.ISO_A2 || props.iso_3166_1_alpha_2 || null;
    };

    const getUsRegionCode = (feature) => {
      const fips = String(feature.id).padStart(2, '0');
      return FIPS_TO_REGION[fips] || null;
    };

    const getCaRegionCode = (feature) => feature.properties?.iso_3166_2 || null;

    /** Look up country label: Shopify data first, then static map, then geo properties.name, then code */
    const getCountryName = (code, feature) => {
      if (!code) return (feature?.properties?.name) || 'Unknown';
      const match = state.countries.find((c) => c.code === code);
      if (match) return match.label;
      if (ISO_CODE_TO_NAME[code]) return ISO_CODE_TO_NAME[code];
      if (feature?.properties?.name) return feature.properties.name;
      return code;
    };

    // ── Color logic ──
    const filtersActive = () => {
      const f = state.filters;
      return (f.search || '').trim() || f.channel || f.segment || f.country || f.region;
    };

    function getFill(code, type) {
      const { country: fc, region: fr } = state.filters;
      if (type === 'country') {
        if (fc) {
          if (fc !== code) return COLORS.dimmed;
          return state.countriesWithFilteredResults.has(code) ? COLORS.selected : COLORS.selectedEmpty;
        }
        return state.countriesWithNonGeoResults.has(code) ? COLORS.hasDealer : COLORS.noDealer;
      }

      // US and Canada are drawn as regions rather than country polygons.
      const parent = type === 'us-state' ? 'US' : 'CA';
      if (fc && fc !== parent) return COLORS.dimmed;
      if (fr) {
        if (fr !== code) return COLORS.dimmed;
        return state.regionsWithFilteredResults.has(code) ? COLORS.selected : COLORS.selectedEmpty;
      }
      if (fc === parent) {
        return state.regionsWithFilteredResults.has(code) ? COLORS.selected : COLORS.selectedEmpty;
      }
      return state.regionsWithNonGeoResults.has(code) ? COLORS.hasDealer : COLORS.noDealer;
    }

    /**
     * Tooltip count: always reflects the active non-geographic filters
     * (channel, search). Selected areas show their fully-filtered count;
     * non-selected areas show the non-geo-filtered count so users can
     * see how many partners match the channel/search in each area.
     */
    function getTooltipCount(code, type) {
      const active = filtersActive();

      if (type === 'country') {
        if (!active) return state.dealersByCountry[code] || 0;
        return state.dealersByCountryNonGeo[code] || 0;
      }

      // us-state or ca-province
      if (!active) return state.dealersByRegion[code] || 0;
      return state.dealersByRegionNonGeo[code] || 0;
    }

    // ── Draw features ──
    function draw() {
      // Countries
      g.selectAll('.feature--country')
        .data(worldFeatures, (d) => d.id)
        .join('path')
        .attr('class', 'feature feature--country')
        .attr('d', pathGen)
        .each(function (d) {
          d._code = getCountryCode(d);
          d._type = 'country';
          d._name = getCountryName(d._code, d);
        })
        .on('click', (event, d) => handleMapClick(d._code, 'country'))
        .on('mousemove', (event, d) => {
          const count = getTooltipCount(d._code, 'country');
          showTooltip(d._name, count, event);
        })
        .on('mouseleave', hideTooltip);

      // US states
      g.selectAll('.feature--us')
        .data(usFeatures, (d) => d.id)
        .join('path')
        .attr('class', 'feature feature--us')
        .attr('d', pathGen)
        .each(function (d) {
          d._code = getUsRegionCode(d);
          d._type = 'us-state';
          d._name = US_STATE_NAMES[d._code] || d.properties?.name || 'Unknown';
        })
        .on('click', (event, d) => handleMapClick(d._code, 'us-state'))
        .on('mousemove', (event, d) => {
          const count = getTooltipCount(d._code, 'us-state');
          showTooltip(d._name, count, event);
        })
        .on('mouseleave', hideTooltip);

      // Canadian provinces
      g.selectAll('.feature--ca')
        .data(caFeatures, (d) => d.properties?.iso_3166_2 || d.properties?.name)
        .join('path')
        .attr('class', 'feature feature--ca')
        .attr('d', pathGen)
        .each(function (d) {
          d._code = getCaRegionCode(d);
          d._type = 'ca-province';
          d._name = d.properties?.name || 'Unknown';
        })
        .on('click', (event, d) => handleMapClick(d._code, 'ca-province'))
        .on('mousemove', (event, d) => {
          const count = getTooltipCount(d._code, 'ca-province');
          showTooltip(d._name, count, event);
        })
        .on('mouseleave', hideTooltip);

      updateColors();
    }

    function updateColors() {
      g.selectAll('.feature--country').attr('fill', (d) => getFill(d._code, 'country'));
      g.selectAll('.feature--us').attr('fill', (d) => getFill(d._code, 'us-state'));
      g.selectAll('.feature--ca').attr('fill', (d) => getFill(d._code, 'ca-province'));

      // Selection outline — only show blue border when the selection has results
      const { country: fc, region: fr } = state.filters;
      const countriesWithResults = state.countriesWithFilteredResults;
      const regionsWithResults = state.regionsWithFilteredResults;
      g.selectAll('.feature').classed('feature--selected', (d) => {
        if (d._type === 'country') {
          if (fc !== d._code || fr) return false;
          return countriesWithResults.has(d._code);
        }
        if (fr !== d._code) return false;
        return regionsWithResults.has(d._code);
      });
    }

    // ── Map click handler ──
    function handleMapClick(code, type) {
      if (!code) return;
      const { country: fc, region: fr } = state.filters;

      if (type === 'country') {
        if (fc === code) {
          setFilters({ country: '', region: '' });
        } else {
          setFilters({ country: code, region: '' });
        }
      } else if (type === 'us-state') {
        if (fr === code) {
          setFilters({ country: 'US', region: '' });
        } else {
          setFilters({ country: 'US', region: code });
        }
      } else if (type === 'ca-province') {
        if (fr === code) {
          setFilters({ country: 'CA', region: '' });
        } else {
          setFilters({ country: 'CA', region: code });
        }
      }

      zoomToSelection();
    }

    // ── Zoom ──
    function zoomToSelection() {
      const { country: fc, region: fr } = state.filters;
      let target = null;

      if (fr) {
        g.selectAll('.feature').each(function (d) { if (d._code === fr) target = d; });
      } else if (fc === 'US') {
        // Fit the contiguous states; Alaska crosses the antimeridian and
        // overseas territories otherwise expand the bounds to nearly the world.
        // Keep every feature on the map and available for individual selection.
        const contiguousStates = usFeatures.filter((feature) => {
          const fips = Number(feature.id);
          return fips < 60 && fips !== 2 && fips !== 15;
        });
        target = { type: 'FeatureCollection', features: contiguousStates };
      } else if (fc === 'CA') {
        target = { type: 'FeatureCollection', features: caFeatures.length ? caFeatures : [] };
        // Fallback to world feature for CA if no provinces loaded
        if (!caFeatures.length) {
          g.selectAll('.feature--country').each(function (d) { if (d._code === 'CA') target = d; });
        }
      } else if (fc) {
        g.selectAll('.feature--country').each(function (d) { if (d._code === fc) target = d; });
      }

      if (target && (target.features ? target.features.length > 0 : true)) {
        const [[x0, y0], [x1, y1]] = pathGen.bounds(target);
        const dx = x1 - x0;
        const dy = y1 - y0;
        const x = (x0 + x1) / 2;
        const y = (y0 + y1) / 2;
        const scale = Math.max(1, Math.min(12, 0.8 / Math.max(dx / width, dy / height)));
        const tx = width / 2 - scale * x;
        const ty = height / 2 - scale * y;

        svg.transition().duration(750)
          .call(zoomBehavior.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
      } else {
        svg.transition().duration(750)
          .call(zoomBehavior.transform, d3.zoomIdentity);
      }
    }

    // ── Resize ──
    function handleResize() {
      const rect = container.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      svg.attr('viewBox', `0 0 ${width} ${height}`);
      projection.scale(width / 5.5).translate([width / 2, height / 2]);
      pathGen = d3.geoPath().projection(projection);
      g.selectAll('path').attr('d', pathGen);
    }

    // ── Subscribe to state changes ──
    let prevCountry = '';
    let prevRegion = '';
    subscribe(() => {
      updateColors();
      const { country: fc, region: fr } = state.filters;
      if (fc !== prevCountry || fr !== prevRegion) {
        prevCountry = fc || '';
        prevRegion = fr || '';
        zoomToSelection();
      }
    });
    window.addEventListener('resize', handleResize);

    // ── Initialize ──
    setup();
    loadGeo().then(() => {
      draw();
      if (typeof onMapReady === 'function') onMapReady();
    }).catch((err) => {
      console.error('Partner Locator: Failed to load map data', err);
      if (typeof onMapReady === 'function') onMapReady();
    });

    return { zoomToSelection };
  }

  // ════════════════════════════════════════════════════════════════
  // FILTERS UI
  // ════════════════════════════════════════════════════════════════

  function initFilters(root) {
    const sid = root.getAttribute('data-section-id');
    const els = {
      search:  root.querySelector(`#pl2-search-${sid}`),
      channel: root.querySelector(`#pl2-channel-${sid}`),
      segment: root.querySelector(`#pl2-segment-${sid}`),
      country: root.querySelector(`#pl2-country-${sid}`),
      region:  root.querySelector(`#pl2-region-${sid}`),
      reset:   root.querySelector('.pl2__reset'),
      mapReset: root.querySelector('.pl2__map-reset'),
      regionField: root.querySelector('.pl2__field--region'),
      segmentField: root.querySelector('.pl2__field--segment'),
    };

    /** Segment is a sub-filter of the Marine channel. */
    const isMarineChannel = (ch) => String(ch || '').trim().toLowerCase() === 'marine';

    function populateDropdowns() {
      const channels = uniqSorted(state.dealers.flatMap((d) => d.channel));
      const countries = state.countries.slice().sort((a, b) => a.label.localeCompare(b.label));

      setSelectOpts(els.channel, channels, 'All');

      // Countries dropdown — value=code, display=label
      const cur = els.country.value;
      els.country.innerHTML =
        '<option value="">All</option>' +
        countries.map((c) => `<option value="${esc(c.code)}">${esc(c.label)}</option>`).join('');
      if (countries.some((c) => c.code === cur)) els.country.value = cur;
    }

    function updateRegionDropdown() {
      const country = state.filters.country;
      if (country !== 'US' && country !== 'CA') {
        els.region.disabled = true;
        els.region.innerHTML = '<option value="">—</option>';
        els.region.value = '';
        return;
      }

      const regions = state.regions
        .filter((r) => r.country === country)
        .sort((a, b) => a.label.localeCompare(b.label));

      const cur = els.region.value;
      els.region.disabled = false;
      els.region.innerHTML =
        '<option value="">All</option>' +
        regions.map((r) => `<option value="${esc(r.code)}">${esc(r.label)}</option>`).join('');
      if (regions.some((r) => r.code === cur)) els.region.value = cur;
    }

    /** Segment dropdown: disabled unless the Marine channel is selected. */
    function updateSegmentDropdown() {
      if (!els.segment) return;
      const channel = state.filters.channel;
      if (!isMarineChannel(channel)) {
        els.segment.disabled = true;
        els.segment.innerHTML = '<option value="">—</option>';
        els.segment.value = '';
        return;
      }

      const segments = uniqSorted(
        state.dealers
          .filter((d) => d.channel.includes(channel))
          .flatMap((d) => d.segment)
      );

      const cur = els.segment.value;
      els.segment.disabled = segments.length === 0;
      els.segment.innerHTML =
        '<option value="">All</option>' +
        segments.map((s) => `<option value="${esc(s)}">${esc(s)}</option>`).join('');
      if (segments.includes(cur)) els.segment.value = cur;
    }

    function updateReset() {
      const f = state.filters;
      const active = f.search || f.channel || f.segment || f.country || f.region;
      els.reset.style.display = active ? '' : 'none';
      if (els.mapReset) els.mapReset.style.display = active ? '' : 'none';
    }

    /** Sync the UI controls to match current state.filters */
    function syncFromState() {
      els.search.value = state.filters.search;
      els.channel.value = state.filters.channel;
      updateSegmentDropdown();
      if (els.segment) els.segment.value = state.filters.segment;
      els.country.value = state.filters.country;
      updateRegionDropdown();
      els.region.value = state.filters.region;
      updateReset();
    }

    // ── UI → state events ──
    let debounce;
    els.search.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => setFilters({ search: els.search.value.trim() }), 180);
    });
    els.channel.addEventListener('change', () => setFilters({ channel: els.channel.value, segment: '' }));
    if (els.segment) {
      els.segment.addEventListener('change', () => setFilters({ segment: els.segment.value }));
    }
    els.country.addEventListener('change', () => setFilters({ country: els.country.value, region: '' }));
    els.region.addEventListener('change', () => setFilters({ region: els.region.value }));
    els.reset.addEventListener('click', (e) => {
      e.preventDefault();
      setFilters({ search: '', channel: '', segment: '', country: '', region: '' });
      if (typeof state.zoomToSelection === 'function') state.zoomToSelection();
    });
    if (els.mapReset) {
      els.mapReset.addEventListener('click', () => {
        setFilters({ search: '', channel: '', segment: '', country: '', region: '' });
        if (typeof state.zoomToSelection === 'function') state.zoomToSelection();
      });
    }

    // Subscribe to state → UI sync
    subscribe(syncFromState);

    return { populateDropdowns, syncFromState };
  }

  // ════════════════════════════════════════════════════════════════
  // DEALER LIST
  // ════════════════════════════════════════════════════════════════

  function initList(root) {
    const elList = root.querySelector('.pl2__list');
    const elCount = root.querySelector('.pl2__count');
    const noResultsMsg = root.getAttribute('data-no-results') || "Can't find a partner? Contact our Inside Sales team.";
    const emptyCtaUrl = root.getAttribute('data-empty-cta-url') || '';
    const emptyCtaLabel = root.getAttribute('data-empty-cta-label') || 'Contact us';

    const slugifyPartnerName = (value) => String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const buildPartnerWebsiteUrl = (rawUrl, partnerName) => {
      let url = String(rawUrl || '').trim();
      if (!url) return '';
      if (!/^https?:\/\//.test(url)) url = 'https://' + url;

      try {
        const parsed = new URL(url);
        parsed.searchParams.set('utm_source', 'sonance');
        parsed.searchParams.set('utm_medium', 'partner_page');
        parsed.searchParams.set('utm_campaign', 'partners_directory');
        if (partnerName) parsed.searchParams.set('utm_content', slugifyPartnerName(partnerName));
        return parsed.toString();
      } catch (_) {
        return url;
      }
    };

    const trackPartnerEvent = (eventName, payload) => {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
        return;
      }

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: eventName,
          ...payload
        });
      }
    };

    function renderCard(d) {
      const channel = d.channel.length
        ? `<div class="pl2__card-channel">${esc(d.channel.join(' · '))}</div>` : '';
      const address = d.address
        ? `<div class="pl2__card-meta">${esc(d.address)}</div>` : '';
      let phone = '';
      if (d.phone) {
        const tel = d.phone.replace(/[^\d+]/g, '');
        phone = `<div class="pl2__card-meta">Phone: <a class="pl2__card-link" data-partner-phone-link="true" data-partner-name="${esc(d.name || 'Partner')}" data-partner-phone="${esc(tel)}" href="tel:${esc(tel)}">${esc(d.phone)}</a></div>`;
      }
      let website = '';
      if (d.website) {
        const url = buildPartnerWebsiteUrl(d.website, d.name);
        website = `<div class="pl2__card-meta"><a class="pl2__card-link pl2__card-website" data-partner-website-link="true" data-partner-name="${esc(d.name || 'Partner')}" data-partner-url="${esc(url)}" href="${esc(url)}" target="_blank" rel="noopener">Website</a></div>`;
      }

      return `<div class="pl2__card">
        <div class="pl2__card-header">
          <h3 class="pl2__card-name">${esc(d.name || 'Partner')}</h3>
        </div>
        ${channel}${address}${phone}${website}
      </div>`;
    }

    function render() {
      const stillLoading = state.loading || !state.mapReady;
      root.classList.toggle('pl2--loading', stillLoading);

      const list = state.filtered;
      const count = list.length;

      elCount.textContent = `${count} partner${count !== 1 ? 's' : ''}`;

      if (state.loading) {
        elList.innerHTML = '';
        return;
      }

      if (count === 0) {
        let ctaHtml = '';
        if (emptyCtaUrl) {
          ctaHtml = `<a href="${esc(emptyCtaUrl)}" class="theme-button theme-button--solid">${esc(emptyCtaLabel)}</a>`;
        }
        elList.innerHTML = `<div class="pl2__empty">${esc(noResultsMsg)}${ctaHtml ? `<div class="pl2__empty-cta">${ctaHtml}</div>` : ''}</div>`;
        return;
      }

      elList.innerHTML = list.map(renderCard).join('');
    }

    elList.addEventListener('click', (event) => {
      const websiteLink = event.target.closest('[data-partner-website-link]');
      if (websiteLink) {
        trackPartnerEvent('partner_website_click', {
          partner_name: websiteLink.dataset.partnerName || '',
          link_url: websiteLink.dataset.partnerUrl || websiteLink.href || '',
          page_location: window.location.href
        });
        return;
      }

      const phoneLink = event.target.closest('[data-partner-phone-link]');
      if (phoneLink) {
        trackPartnerEvent('partner_phone_click', {
          partner_name: phoneLink.dataset.partnerName || '',
          phone_number: phoneLink.dataset.partnerPhone || '',
          page_location: window.location.href
        });
      }
    });

    subscribe(render);

    return { render };
  }

  // ════════════════════════════════════════════════════════════════
  // INIT
  // ════════════════════════════════════════════════════════════════

  async function init() {
    const root = document.querySelector('.section-partner-locator-v2 .pl2, .pl2[data-section-id]');
    if (!root) return;

    // Snapshot URL params immediately, before any async work or subscribers
    // can fire syncUrl() and strip the query string.
    const urlState = parseUrlState();

    // Initialize UI components
    const filters = initFilters(root);
    const list = initList(root);

    // Initialize map (and set mapReady when geo has loaded)
    const mapContainer = root.querySelector('.pl2__map');
    let map = null;
    if (mapContainer) {
      const setMapReady = () => {
        state.mapReady = true;
        notify();
      };
      map = initMap(mapContainer, setMapReady);
      if (map && map.zoomToSelection) state.zoomToSelection = map.zoomToSelection;
    } else {
      state.mapReady = true;
    }

    // Subscribe URL sync
    subscribe(syncUrl);

    // Load data
    try {
      const data = await loadAllData();
      state.dealers = data.dealers;
      state.countries = data.countries;
      state.regions = data.regions;
      state.loading = false;

      // Build dealer presence maps (for map coloring)
      buildDealerMaps();

      // Populate filter dropdowns now that data is ready
      filters.populateDropdowns();

      // Apply initial state from URL (captured at top of init)
      setFilters(urlState);

      // Always notify so loading UI clears (setFilters only notifies when filters change;
      // with no URL params nothing changes, so subscribers never ran)
      notify();

      // If URL had a selection, zoom the map
      if (map && (urlState.country || urlState.region)) {
        // Small delay so the map has drawn features
        setTimeout(() => map.zoomToSelection(), 100);
      }
    } catch (err) {
      console.error('Partner Locator: Failed to load data', err);
      state.loading = false;
      const elList = root.querySelector('.pl2__list');
      if (elList) {
        elList.innerHTML = '<div class="pl2__empty">Error loading partners. Please try again later.</div>';
      }
    }
  }

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
